import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Button, Icon, Link, Loader, QueryBoundary, Size } from '@outdoria/ui';
import { ActionBar, Table, TableHeader } from '../../containers';
import {
  useAdminDeleteGatheringSpot,
  useGetGatheringSpotsAll,
  type GatheringSpotWithTypeName,
} from '@outdoria/api';
import { Form } from '../../components';
import { CDN_URL } from '../../env';

import s from './GatheringSpotsView.module.scss';

export const GatheringSpotsView = () => (
  <QueryBoundary fallback={<Loader />}>
    <Load />
  </QueryBoundary>
);

const Load = () => {
  const navigate = useNavigate();

  const { data: gatheringSpots } = useGetGatheringSpotsAll();
  const { tableHeaders, tableRows } = useTable(gatheringSpots);

  const onGatheringSpotCreate = () => {
    navigate('create');
  };

  return (
    <div className={s.view}>
      <ActionBar
        buttons={
          <Button
            label="Create new gathering spot"
            onClick={onGatheringSpotCreate}
          />
        }
      />

      <Table tableHeaders={tableHeaders} items={tableRows} />
    </div>
  );
};

const useTable = (gatheringSpots: GatheringSpotWithTypeName[]) => {
  const tableHeaders: TableHeader<GatheringSpotWithTypeName>[] = [
    { id: 'name', label: 'Name' },
    { id: 'gathering_spot_type_id', label: 'Gathering spot type' },
    { id: 'description', label: 'Description' },
    { id: 'rarity_id', label: 'Rarity' },
    { id: 'difficulty', label: 'Difficulty (1-10)' },
    { id: 'stack_max', label: 'Max stacks' },
    { id: 'stack_cooldown', label: 'Stack cooldown (min)' },
    { id: 'actions', label: 'Actions' },
  ];

  const tableRows = gatheringSpots.map(
    (gatheringSpot: GatheringSpotWithTypeName) => [
      <Table.Cell
        content={
          <>
            <Form.Image
              src={`${CDN_URL}/${gatheringSpot.thumbnail}`}
              size={Size.SMALL}
            />
            {gatheringSpot.name}
          </>
        }
      />,
      <Table.Cell
        content={
          <Link to={`/spots/types/${gatheringSpot.gathering_spot_type_id}`}>
            {gatheringSpot.gathering_type_name}
          </Link>
        }
      />,
      <Table.Cell content={gatheringSpot.description} />,
      <Table.Cell content={gatheringSpot.rarity_id} />,
      <Table.Cell content={gatheringSpot.difficulty} />,
      <Table.Cell content={gatheringSpot.stack_max} />,
      <Table.Cell content={gatheringSpot.stack_cooldown} />,
      <Table.Cell content={<Actions id={gatheringSpot.gathering_spot_id} />} />,
    ],
  );

  return { tableHeaders, tableRows };
};

const Actions = ({ id }: { id: string }) => {
  const navigate = useNavigate();
  const { mutate, error, isSuccess } = useAdminDeleteGatheringSpot();

  useEffect(() => {
    if (isSuccess) toast.success('Yay! Deleted :3');
  }, [isSuccess]);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  const onDelete = () => {
    const shouldDelete = confirm(
      "Do you really want to delete this gathering spot? When you do that, all markers with this gathering spot's ID will also be removed!",
    );
    if (shouldDelete) mutate(id);
  };

  return (
    <>
      <Button
        label={<Icon icon="Edit" size={Size.MICRO} />}
        onClick={() => navigate(id)}
        style={{ width: '40px' }}
      />
      <Button
        label={<Icon icon="Trash" size={Size.MICRO} />}
        onClick={onDelete}
        variant={Button.Variant.DANGER}
        style={{ width: '40px' }}
      />
    </>
  );
};
